import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useFavorites } from '../../hooks/useFavourites';
import { HeartIcon as HeartOutline } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';

export default function Stores() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredStores, setFilteredStores] = useState([]);
  const { user } = useAuth();
  const { toggleStoreFavorite, isStoreFavorite } = useFavorites();

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const { data } = await api.get('/stores');
        const stores = data.data.stores;

        const storesWithCounts = await Promise.all(
          stores.map(async (store) => {
            try {
              const productsResponse = await api.get('/products', {
                params: { store: store._id, limit: 1 }
              });
              return {
                ...store,
                productCount: productsResponse.data.data.pagination.total
              };
            } catch (err) {
              return { ...store, productCount: 0 };
            }
          })
        );

        setStores(storesWithCounts);
        setFilteredStores(storesWithCounts);
      } catch (err) {
        console.error('Failed to fetch stores:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStores();
  }, []);

  const handleFavoriteToggle = async (storeId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      // Redirect to login or show message
      return;
    }

    try {
      await toggleStoreFavorite(storeId);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredStores(stores);
    } else {
      setFilteredStores(
        stores.filter(store =>
          store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          store.description?.toLowerCase().includes(searchTerm.toLowerCase())
      ));
    }
  }, [searchTerm, stores]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Browse Stores</h1>
          <p className="mt-2 text-sm text-gray-600">
            Discover unique fashion stores created by independent designers
          </p>
        </div>

        <div className="mb-6">
          <div className="relative rounded-md shadow-sm">
            <input
              type="text"
              placeholder="Search stores..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pr-10 sm:text-sm border-gray-300 rounded-md p-3 border focus:ring-pink-500 focus:border-pink-500"
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
          </div>
        ) : filteredStores.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredStores.map((store) => (
              <Link
                key={store._id}
                to={`/store/${store.slug}`}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition duration-300 relative"
              >
          <button
            onClick={(e) => handleFavoriteToggle(store._id, e)}
            className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 z-10"
          >
            {isStoreFavorite(store._id) ? (
              <HeartSolid className="h-5 w-5 text-pink-600" />
            ) : (
              <HeartOutline className="h-5 w-5 text-gray-400" />
            )}
          </button>
                <div className="h-48 bg-gray-200 relative overflow-hidden">
                  {store.banner ? (
                    <img
                      src={store.banner}
                      alt={store.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                      <span className="text-white text-xl font-bold">{store.name}</span>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full overflow-hidden bg-gray-200">
                      {store.logo ? (
                        <img src={store.logo} alt={store.name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full bg-gray-300 flex items-center justify-center">
                          <span className="text-gray-500 text-sm">{store.name.charAt(0)}</span>
                        </div>
                      )}
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">{store.name}</h3>
                      <p className="text-sm text-gray-500">
                        {store.owner?.firstName} {store.owner?.lastName}
                      </p>
                    </div>
                  </div>
                  {store.description && (
                    <p className="mt-3 text-sm text-gray-600 line-clamp-2">
                      {store.description}
                    </p>
                  )}
                  <div className="mt-4 flex justify-between items-center">
                    <span className="text-xs font-medium text-pink-600">
                      {store.productCount || 0} products
                    </span>
                    <div className="flex items-center">
                      <svg className="h-4 w-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      <span className="ml-1 text-xs text-gray-600">
                        {store.rating?.toFixed(1) || 'New'}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">No stores found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm.trim() !== '' 
                ? 'Try adjusting your search or filter to find what you\'re looking for.'
                : 'There are currently no stores available.'}
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
              >
                Clear search
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}